import { create } from 'zustand';
import agent from '../api/agent';
import { Stand } from '../models/stands/stand';
import { createBaseStore } from './util/createBaseStore';
import { EntityStore } from './util/interfaces/entityStore';
import { byKeyResults, OfflineStoreKeys } from '../offlinestore/offline.util';

type StandStore = EntityStore<Stand> & {
    fetchStandsByCustomerId: (customerId: string) => Promise<Stand[]>;
    fetchStandsByEventId: (eventId: string, updateStore: boolean) => Promise<Stand[]>;
    customerStands: Stand[];
    eventStands: Stand[];
    stands: Stand[];
    standsByEvent: Record<string, Stand[]>;
    fetchStandsByEventIds: (eventIds: string[]) => Promise<void>;
    setStands: (stands: Stand[]) => void;
}

const useStandStore = create<StandStore>((set, get) => ({
    ...createBaseStore<Stand>(agent.Stands, OfflineStoreKeys.STANDS)(set, get, {} as any),
    customerStands: [],
    eventStands: [],
    stands: [],
    standsByEvent: {},
    fetchStandsByCustomerId: async (customerId: string) => {
        set({ isLoading: true, error: null });
        try {
            const stands = await agent.Stands.getByCustomerId(customerId);
            const offlineStands = await byKeyResults(stands, 'customerId', customerId, get().storeKey);
            set({ customerStands: navigator.onLine ? stands : offlineStands });
            return navigator.onLine ? stands : offlineStands;
        } catch (error) {
            const offlineStands = await byKeyResults([], 'customerId', customerId, get().storeKey);
            set({ customerStands: offlineStands });
        } finally {
            set({ isLoading: false });
        }
    },
    fetchStandsByEventId: async (eventId: string, updateStore: boolean) => {
        set({ isLoading: true, error: null });
        try {
            const stands = await agent.Stands.getAllByEventId(eventId);
            const offlineStands = await byKeyResults(stands, 'eventId', eventId, get().storeKey);
            if (updateStore) set({ eventStands: navigator.onLine ? stands : offlineStands });
            return navigator.onLine ? stands : offlineStands;
        } catch (error) {
            const offlineStands = await byKeyResults([], 'eventId', eventId, get().storeKey);
            if (updateStore) set({ eventStands: offlineStands });
        } finally {
            set({ isLoading: false });
        }
    },
    fetchStandsByEventIds: async (eventIds: string[]) => {
        set({ isLoading: true, error: null });
        const standsByEvent: Record<string, Stand[]> = {};
        try {
            for (const eventId of eventIds) {
                const stands = await agent.Stands.getAllByEventId(eventId);
                standsByEvent[eventId] = stands || [];
            }
            set({ standsByEvent });
        } catch (error) {
            set({ standsByEvent: {} });
        } finally {
            set({ isLoading: false });
        }
    },
    setStands: (stands: Stand[]) => {
        set({ stands });
    },
}));

export default useStandStore;
